import React from 'react';
import { Typography, Icon, Button, Autocomplete } from '@scale/scaleui';
import {
  NegativePatternGroup,
  NLPConditionPatternGroup,
  PositivePatternGroup,
} from '@scale/shared/task_lint/linters/categorization';
import { TextCollectionField } from '@scale/shared/types/params/textcollection';
import { getValidValuesForFieldId } from '@scale/shared/textcollection/helpers';

const StringPatternBuilder: React.FC<{
  pattern: string;
  updatePattern: (newPattern: string) => void;
  deletePattern: () => void;
  options: string[];
}> = ({ pattern, updatePattern, deletePattern, options }) => {
  return (
    <div className="flex flex-row gap-2 items-center">
      <Autocomplete
        options={options}
        value={pattern}
        setSelectedOption={selectedOption => {
          updatePattern(selectedOption || '');
        }}
      />
      <Icon icon="trash" variant="danger" size="sm" onClick={() => deletePattern()} />
    </div>
  );
};

const ConditionPatternGroupBuilder: React.FC<{
  fieldId: string;
  patternGroup: NLPConditionPatternGroup;
  updatePatternGroup: (newPatternGroup: NLPConditionPatternGroup) => void;
  deletePatternGroup: () => void;
  fields?: Array<TextCollectionField>;
}> = ({ fieldId, patternGroup, updatePatternGroup, deletePatternGroup, fields }) => {
  const isPositive = Array.isArray(patternGroup);
  const phrase = isPositive
    ? 'IS ONE OF THE FOLLOWING VALUES'
    : 'IS NOT ONE OF THE FOLLOWING VALUES';
  const group = isPositive
    ? (patternGroup as PositivePatternGroup)
    : (patternGroup as NegativePatternGroup).not;

  const setSinglePattern = (value: string, index: number) => {
    const newPatternGroup = [...group];
    newPatternGroup[index] = value;
    updatePatternGroup(isPositive ? newPatternGroup : { not: newPatternGroup });
  };
  const removeSinglePattern = (index: number) => {
    const newPatternGroup = [...group];
    newPatternGroup.splice(index, 1);
    updatePatternGroup(isPositive ? newPatternGroup : { not: newPatternGroup });
  };
  const addSinglePattern = () => {
    const newPatternGroup = [...group];
    newPatternGroup.push('');
    updatePatternGroup(isPositive ? newPatternGroup : { not: newPatternGroup });
  };

  const options: string[] = React.useMemo(() => {
    if (!fields) {
      return [];
    }
    const valuesObject = getValidValuesForFieldId(fields, fieldId);
    return valuesObject?.values?.map(v => v.toString()) || [];
  }, [fields, fieldId]);
  return (
    <div className="rounded-lg shadow bg-neutral-0 h-fit p-4 mb-2">
      <div className="flex flex-row gap-2 items-center">
        <Typography.Paragraph2>{fieldId}</Typography.Paragraph2>
        <Icon icon="trash" variant="danger" size="sm" onClick={() => deletePatternGroup()} />
      </div>
      <div className="flex flex-col gap-2">
        <div>{phrase}</div>
        {group.map((value, i) => (
          // TODO: add support for regex and wildcard values
          <StringPatternBuilder
            key={i}
            pattern={value.toString()}
            updatePattern={(newPattern: string) => setSinglePattern(newPattern, i)}
            deletePattern={() => removeSinglePattern(i)}
            options={options}
          />
        ))}
        <div>
          <Button onClick={() => addSinglePattern()}>Add String Pattern</Button>
        </div>
      </div>
    </div>
  );
};

export default ConditionPatternGroupBuilder;
